import { createSlice } from '@reduxjs/toolkit';

import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

/**
 * Redux twin of lib/state/Timer.ts (JSAD-7433). The `setInterval` handle the Zustand slice
 * kept in state (`intervalId`) is replaced by `running`: `timerSaga` owns the interval
 * (`takeLeading(startRequest)` → `race({ loop: delay(1000) + put(tick), stop: take(stopRequest) })`)
 * and dispatches `started` / `stopped`. Not reset on `auth/loggedOut`.
 */
export interface TimerState {
    seconds: number;
    running: boolean;
}

const initialState: TimerState = {
    seconds: 0,
    running: false
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

const timerSlice = createSlice({
    name: 'timer',
    initialState,
    reducers: {
        tick: (state) => {
            state.seconds += 1;
        },
        started: (state) => {
            state.running = true;
        },
        stopped: (state) => {
            state.running = false;
        },
        /** `Timer.ts:52` — stops AND zeroes the counter. */
        resetTimer: () => initialState,

        // ── Saga-only requests (`start` / `stop` own the interval) ─────────────────────────
        startRequest: request<void>(),
        stopRequest: request<void>()
    }
});

export const { tick, started, stopped, resetTimer, startRequest, stopRequest } = timerSlice.actions;

export const selectTimer = (state: { timer: TimerState }) => state.timer;
export const selectTimerSeconds = (state: { timer: TimerState }) => state.timer.seconds;
export const selectTimerRunning = (state: { timer: TimerState }) => state.timer.running;

export default timerSlice.reducer;
